import React, { useEffect, useState, useRef, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import { Container, CardTitle } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Board from '@asseinfo/react-kanban';
import _ from 'lodash';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from 'hooks/useAuth';
import KanbanCard from './components/Kanban-Card';
import ChangelogModal from '../../components/Changelog-Modal';
import TaskModal from './components/Task-Modal';
import TaskModalContext from './components/Task-Modal/TaskModal.Context';
import KanbanBoardShimmer from '../../components/Kanban-Board-Shimmer';
import Breadcrumbs from '../../components/Breadcrumb';
import '../../assets/scss/custom/components/_shimmer.scss';
import '@asseinfo/react-kanban/dist/styles.css';

import axios from 'axios';
import { getAuth } from 'firebase/auth';
import KanbanHeader from './components/Kanban-Header';
import HeaderContext from './components/Tasks-Filter/TasksFilter.Context';

const KanbanBoard = () => {
  const { t: translate } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const location = window.location;
  const accountId = user?.account;
  const userStatus = user?.adminStatus;
  const { adminStatus } = user;
  const [board, setBoard] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [lastColumnTasks, setLastColumnTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [members, setMembers] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [filterTotal, setFilterTotal] = useState(0);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalId, setModalId] = useState(false);
  const [modalData, setModalData] = useState({});

  const taskParam = new URLSearchParams(location.search).get('id');

  useEffect(() => {
    if (!accountId) return;

    const fetchData = async () => {
      setLoading(true);

      if (taskParam) openCardModal({ id: taskParam });

      const auth = getAuth();
      const user = auth.currentUser;
      await user.getIdToken().then((token) => {
        axios
          .get(`${process.env.REACT_APP_API_URL}/tasks?accountId=${accountId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then(async (response) => {
            setAllTasks(response.data.tasks);
            setStatuses(response.data.statuses);
          })
          .catch((error) => {
            console.error('Error fetching tasks:', error);
          });

        axios
          .get(`${process.env.REACT_APP_API_URL}/closedTasks?accountId=${accountId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            setLastColumnTasks(response.data.tasks);
          })
          .catch((error) => {
            console.error('Error fetching closed tasks:', error);
          });

        axios
          .get(`${process.env.REACT_APP_API_URL}/options?accountId=${accountId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            const { focuses, members } = response.data;
            setFocuses(focuses);
            setMembers(members);
          })
          .catch((error) => {
            console.error('Error fetching options:', error);
          });
      });
    };

    fetchData();
  }, [accountId]);

  const normalizeDate = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const processTasks = async (receivedTasks, statuses) => {
    if (!receivedTasks || !statuses) return null;

    const initialColumns = Object.values(statuses)
      .filter((status) => !hiddenColumns.includes(status.id))
      .map((status) => ({
        id: status.id,
        title: status.status,
        cards: [],
      }))
      .sort((a, b) => a.orderindex - b.orderindex);

    const taskState = _.cloneDeep(initialColumns);

    const sortedTasks = Object.entries(receivedTasks)
      .sort((a, b) => a[1]['orderindex'] - b[1]['orderindex'])
      .filter(([, taskValue]) => {
        if (!filter) return true;
        let totalFilters = 0;
        let assignedBool = 1;
        let impactBool = 1;
        let foundationsBool = 1;
        let searchBool = 1;
        let createDateBool = 1;
        let implementationDateBool = 1;
        let deliveryDateBool = 1;

        if (!_.isEmpty(filter.assignedTo)) {
          assignedBool = 0;
          totalFilters++;
          filter.assignedTo.forEach(({ value }) => {
            if (
              Array.isArray(taskValue.assignees) &&
              taskValue.assignees.find((assignee) => assignee.id === value)
            ) {
              assignedBool++;
            }
          });
        }

        if (!_.isEmpty(filter.priority)) {
          impactBool = 0;
          filter.priority.forEach(({ value }) => {
            totalFilters++;
            if (taskValue.priority.priority === value) impactBool++;
          });
        }

        if (!_.isEmpty(filter.focuses)) {
          foundationsBool = 0;
          filter.focuses.forEach(({ value }) => {
            totalFilters++;
            const taskFocuses =
              taskValue.custom_fields?.find((field) => field.name === 'Foco')?.value || [];
            if (Array.isArray(taskFocuses) && taskFocuses.includes(value)) {
              foundationsBool++;
            }
          });
        }

        if (filter.search) {
          searchBool = 0;
          totalFilters++;
          const normalizedSearch = filter.search
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
          const normalizedName = taskValue.name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
          const normalizedDescription = (taskValue?.description || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
          if (normalizedName.includes(normalizedSearch)) searchBool++;
          if (normalizedDescription.includes(normalizedSearch)) searchBool++;
        }

        if (filter.startStartDate && filter.endStartDate) {
          totalFilters++;
          createDateBool = 0;

          const taskDate = normalizeDate(+taskValue.start_date);
          const startDate = normalizeDate(filter.startStartDate);
          const endDate = normalizeDate(filter.endStartDate);

          if (
            taskDate !== null &&
            startDate !== null &&
            endDate !== null &&
            taskDate >= startDate &&
            taskDate <= endDate
          ) {
            createDateBool++;
          }
        }

        if (filter.startImplementationDate && filter.endImplementationDate) {
          totalFilters++;
          implementationDateBool = 0;

          const taskDate = normalizeDate(
            +taskValue.custom_fields?.find((field) => field.name === 'Data de implementação')
              ?.value,
          );
          const startDate = normalizeDate(filter.startImplementationDate);
          const endDate = normalizeDate(filter.endImplementationDate);

          if (
            taskDate !== null &&
            startDate !== null &&
            endDate !== null &&
            taskDate >= startDate &&
            taskDate <= endDate
          ) {
            implementationDateBool++;
          }
        }

        if (filter.startDeliveryDate && filter.endDeliveryDate) {
          totalFilters++;
          deliveryDateBool = 0;

          const taskDate = normalizeDate(+taskValue.due_date);
          const startDate = normalizeDate(filter.startDeliveryDate);
          const endDate = normalizeDate(filter.endDeliveryDate);

          if (
            taskDate !== null &&
            startDate !== null &&
            endDate !== null &&
            taskDate >= startDate &&
            taskDate <= endDate
          ) {
            deliveryDateBool++;
          }
        }

        setFilterTotal(totalFilters);

        return (
          assignedBool &&
          impactBool &&
          foundationsBool &&
          searchBool &&
          createDateBool &&
          implementationDateBool &&
          deliveryDateBool
        );
      })
      .map((entry) => entry[1]);

    for (const value of Object.values(sortedTasks)) {
      const foundColumn = taskState.find((column) => column.title === value.status.status);
      if (foundColumn) {
        foundColumn.length = (foundColumn?.length || 0) + 1;
        foundColumn.cards.push({ ...value, position: foundColumn.cards?.length || 0 });
      }
    }

    return taskState;
  };

  useEffect(() => {
    if (!allTasks || !statuses) return;

    const filterTasks = async () => {
      const processedTasks = await processTasks([...allTasks, ...lastColumnTasks], statuses);
      if (processedTasks) {
        setBoard(() => ({ columns: processedTasks }));
        setLoading(false);
      }
    };

    filterTasks();
  }, [filter, allTasks, statuses, hiddenColumns, lastColumnTasks]);

  const openCardModal = useCallback(
    async (data) => {
      setModalId(data.id);
      setModalData(data);
      setModal(true);
    },
    [accountId],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Kanban')} | Ectools`}</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title={translate('Tasks')} breadcrumbItem={translate('Kanban Board')} />

          <ChangelogModal />

          <TaskModalContext.Provider
            value={{ adminStatus, modal, setModal, id: modalId, data: modalData }}
          >
            <TaskModal accountId={accountId} />
          </TaskModalContext.Provider>

          {!loading && (
            <HeaderContext.Provider
              value={{ setFilter, members, focuses, statuses, hiddenColumns, setHiddenColumns }}
            >
              <KanbanHeader
                board={board}
                accountId={accountId}
                filterTotal={filterTotal}
                userStatus={userStatus}
              />
            </HeaderContext.Provider>
          )}

          {!loading && (
            <div style={{ height: '64.9vh' }}>
              <Board
                disableColumnDrag
                disableCardDrag
                renderColumnHeader={({ title, length }) => (
                  <div className="d-flex justify-content-between">
                    <CardTitle className="mb-4 text-capitalize">
                      {translate(title)} ({length || 0})
                    </CardTitle>
                  </div>
                )}
                renderCard={(data) => (
                  <KanbanCard data={data} action={openCardModal}>
                    {data}
                  </KanbanCard>
                )}
              >
                {board}
              </Board>
            </div>
          )}
          {loading && <KanbanBoardShimmer />}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default KanbanBoard;
